import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AngularFirestore } from '@angular/fire/firestore';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import * as firebase from 'firebase/app';
import 'firebase/storage';
import { TestCredit, Request, CreditBatch, Device, ControlsBatch } from '../globals/globals';
import { AuthService } from '../core/auth.service';
import { AngularFireAuth } from '@angular/fire/auth';
import { EmailNotAvailableError } from '../_helpers/email-not-available-error';
import { formatDate } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class HttpService implements OnDestroy {

  db = firebase.firestore();
  currentAdmin = {
    uid: '',
    email: '',
    name: '',
    adminType: '',
    userID: '',
    distributorUseType: '',
    distributorSiteAccess: ''
  };

  distributor: any;

  private apiEP: string;
  private userSubscription: Subscription;
  private adminReady: Promise<void>;
  private adminReadyResolve: () => void;
  private allCustomersCache: Promise<any[]> = null;
  private lastAdminEmail: string = '';
  apiUrl = 'https://ioanalyticservices-ldoxyjbqhq-uc.a.run.app';

  constructor(private _http: HttpClient, private afAuth: AngularFireAuth, private afs: AngularFirestore, private authService: AuthService) {
    const hostname = window.location.hostname.toString().toLowerCase();
    if (hostname.includes('localhost')) {
      this.apiEP = 'api';
    } else {
      this.apiEP = "https://us-central1-messqasystem.cloudfunctions.net";
    }
    this.resetAdminReady();
    this.userSubscription = this.authService.user.subscribe((user) => {
      if (user !== null && user[0] !== undefined && user[0] !== null) {
        const newEmail = user[0].email;
        if (this.lastAdminEmail && this.lastAdminEmail !== newEmail) {
          this.allCustomersCache = null;
          this.distributor = null;
          this.resetAdminReady();
        }
        this.lastAdminEmail = newEmail;
        this.currentAdmin.uid = user[0].uid;
        this.currentAdmin.email = newEmail;
        this.currentAdmin.name = user[0].firstName + ' ' + user[0].lastName;
        this.currentAdmin.adminType = user[0].adminType;
        this.currentAdmin.distributorUseType = user[0].distributorUseType || 'active';
        this.currentAdmin.distributorSiteAccess = (user as any)[0].distributorSiteAccess || 'advanced';
        this.adminReadyResolve();
      } else {
        this.allCustomersCache = null;
        this.distributor = null;
        this.lastAdminEmail = '';
        this.resetAdminReady();
      }
    });
  }

  private resetAdminReady() {
    this.adminReady = new Promise(resolve => {
      this.adminReadyResolve = resolve;
    });
  }

  ngOnDestroy() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  // ====================================================================================================
  // Global and Generic functions
  // ====================================================================================================
  public async getCollectionList(collectionpath) {
    const snapshot = await this.afs.collection(collectionpath).get().pipe(take(1)).toPromise();
    const markers: any = [];
    snapshot.forEach(doc => {
      markers.push({ id: doc.id, data: doc.data() });
    });
    return markers;
  }

  public async getFacilitiesForDistributor() {
    if (this.distributor?.data?.mesDistributer?.id == undefined) {
      return [];
    }

    const snapshot = await this.afs.collection('MESDistributers')
      .doc(this.distributor.data.mesDistributer.id).get().pipe(take(1)).toPromise();
    const docData: any = snapshot.data();
    const facilityIds: string[] = [...docData.facilities];

    const useType = this.currentAdmin?.distributorUseType || 'active';
    const customerTypeMap = {
      'active': 'facility',
      'development': 'development',
      'validation': 'validation'
    };
    const customerType = customerTypeMap[useType] || 'facility';

       const promiseAll = [];

    while (facilityIds.length != 0) {
      const batch = facilityIds.splice(0, 10);
      promiseAll.push(
        this.db.collection('SQACustomers').where('uid', 'in', batch).where('customerType', '==', customerType).get()
      );
    }

    const results = await Promise.all(promiseAll);
    const finalData = [];

      results.forEach((ele) => {
        ele.forEach((el) => {
          finalData.push({ id: el.id, data: el.data() });
        });
      });
    
    return finalData;
  }

  public getTestsForAllFacilities(allFacilities) {
    const allPromises = allFacilities.map(facility => this.getAllTestsForFacility(facility.id));
    return Promise.all(allPromises);
  }

  public getTestsByFacilityIds(facilityIds: string[]) {
    const facilitySet = new Set(facilityIds);
    return this.db.collectionGroup('tests').get().then(snapshot => {
      const markers = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.facilityId && facilitySet.has(data.facilityId)) {
          markers.push({ testid: doc.id, testdata: data });
        }
      });
      return markers;
    });
  }

  public async getCollectionData(collectionpath, docid) {
    const doc = await this.afs.collection(collectionpath).doc(docid).get().pipe(take(1)).toPromise();
    if (doc.exists) {
      return { id: doc.id, data: doc.data() };
    } else {
      return { id: undefined, data: undefined };
    }
  }

  public addCollectionItam(collectionpath, data) {
    return new Promise<any>((res, rej) => {
      this.afs.collection(collectionpath).add(data)
        .then((doc) => {
          res(doc);
        })
        .catch((error) => {
          console.error("Error adding document: ", error);
          rej(error);
        });
    });
  }

  public updateCollectionData(collectionpath, Itemid, itemData) {
    return new Promise<any>((res, rej) => {
      this.afs.collection(collectionpath).doc(Itemid).set(itemData, { merge: true })
        .then(() => {
          res({ id: Itemid, data: itemData });
        })
        .catch((error) => {
          console.error("Error updating document: ", error);
          rej(error);
        });
    });
  }

  public deleteCollectionItem(collectionpath, Itemid) {
    return new Promise<any>((res, rej) => {
      this.afs.collection(collectionpath).doc(Itemid).delete()
        .then((docRef) => {
          res(docRef);
        })
        .catch((error) => {
          console.error("Error deleting document: ", error);
          rej(error);
        });
    });
  }

  public async getTotalNumber(collection) {
    const querySnapshot = await this.afs.collection(collection).get().pipe(take(1)).toPromise();
    return { number: querySnapshot.docs.length };
  }

  private uploadFile(file, ref, filetype: string = 'Image') {
    return new Promise<any>((res, rej) => {
      if (file.type.includes(filetype)) {
        ref.put(file).then((snapshot) => {
          snapshot.ref.getDownloadURL().then((url) => {
            res(url);
          }).catch(err => {
            rej(err);
          });
        });
      } else {
        rej("error");
      }
    });
  }

  public getFileUrl(fileName) {
    return new Promise<any>((res, rej) => {
      var ref = firebase.storage().ref().child(`${fileName}`);
      ref.getDownloadURL().then((url) => {
        res(url);
      }).catch(err => {
        rej(err);
      });
    });
  }

  public getSignalData(url) {
    return this._http.get(url, { responseType: 'text' }).pipe(take(1)).toPromise();
  }

  public UploadVideo(currentFacilityId, file: File, videoType) {
    var storageRef = firebase.storage().ref();
    var ref = storageRef.child(`Visualization/Videos/${currentFacilityId}/` + file.name);
    this.addCollectionItam('SQACustomers/' + currentFacilityId + '/Videos', videoType);
    return this.uploadFile(file, ref, 'video/mp4');
  }

  public UploadSignal(path, signalData) {
    var storageRef = firebase.storage().ref();
    var ref = storageRef.child(path);
    return new Promise<any>((res, rej) => {
      ref.putString(signalData).then((snapshot) => {
        snapshot.ref.getDownloadURL().then((url) => {
          res(url);
        }).catch(err => {
          rej(err);
        });
      });
    });
  }

  public UpdateFacilityType(currentFacilityId, facilityType: string) {
    let data = { customerType: facilityType };
    this.updateCollectionData('SQACustomers', currentFacilityId, data);
  }

  public UpdateFacilityProfile(currentFacilityId, data) {
    return this.updateCollectionData('SQACustomers', currentFacilityId, data);
  }

  public gettestsForPatient(currentFacilityId, patientid) {
    return this.getCollectionList('SQACustomers/' + currentFacilityId + '/patients/' + patientid + '/tests');
  }

  public getControltests(currentFacilityId) {
    return this.getCollectionList('SQACustomers/' + currentFacilityId + '/controlTests');
  }

  public getallPatientsForFacility(currentFacilityId) {
    return this.getCollectionList('SQACustomers/' + currentFacilityId + '/patients');
  }

  public getAllTestsForFacility(currentFacilityId) {
    return new Promise<any>((res, rej) => {
      let markers: any = [];
      this.db.collectionGroup('tests').where('facilityId', '==', currentFacilityId).get()
        .then(tests => {
          for (const test of tests.docs) {
            markers.push({ testid: test.id, testdata: test.data() });
          }
          res(markers);
        }).catch(err => {
          rej(err);
        });
    });
  }

  public getTestsPaginate(itemcount, lastVisible) {
    return new Promise<any>((res, rej) => {
      let markers: any = [];
      this.db.collectionGroup('tests')
        .orderBy("facilityId")
        .limit(itemcount)
        .get()
        .then(tests => {
          for (const test of tests.docs) {
            markers.push({ testid: test.id, testdata: test.data() });
          }
          res(markers);
        }).catch(err => {
          rej(err);
        });
    });
  }

  public getFacilityData(currentFacilityId) {
    return this.getCollectionData('SQACustomers', currentFacilityId);
  }

  public getallCustomers() {
    if (!this.allCustomersCache) {
      this.allCustomersCache = this._fetchAllCustomers();
    }
    return this.allCustomersCache;
  }

  private async _fetchAllCustomers() {
    await this.adminReady;

    this.distributor = {};
    const snapshot = await this.db.collection('Distributers')
      .where("email", "==", this.currentAdmin.email).get();

    if (snapshot && snapshot.docs.length > 0) {
      this.distributor.id = snapshot.docs[0].id;
      this.distributor.data = snapshot.docs[0].data();
    }

    return this.getFacilitiesForDistributor();
  }

  public refreshAllCustomers() {
    this.allCustomersCache = null;
    return this.getallCustomers();
  }

  public getCustomersDistributors(customerTypes: string[] = ['facility']) {
    const customers: any = [];
    const distribs: any = [];

    return new Promise<any>((res, rej) => {
      this.db.collection('Distributers').get().then(dSnapshot => {
        dSnapshot.forEach(ddoc => {
          let data = ddoc.data();
          data.id = ddoc.id;
          distribs.push(data);
        });
        return distribs;
      }).then(distributers => {
        this.db.collection('SQACustomers').where("customerType", "in", customerTypes).get().then(snapshot => {
          snapshot.forEach(doc => {
            let addData = doc.data();
            if (doc.data().servicePersons !== undefined && doc.data().servicePersons.length > 0) {
              addData.distributers = doc.data().servicePersons.map(pp => ({
                id: pp.id, support: pp.support, order: pp.order, data: distributers.find(obj => {
                  return obj.id === pp.id;
                })
              }));
            } else {
              addData.distributers = "NA";
            }
            customers.push({ id: doc.id, data: addData });
          });
          res(customers);
        });
      });
    });
  }

  public UpdatePatientData(currentFacilityId, patientid, data) {
    let path = 'SQACustomers/' + currentFacilityId + '/patients/';
    this.updateCollectionData(path, patientid, data);
  }

  UploadProfilePicture(currentFacilityId, file: File) {
    var storageRef = firebase.storage().ref();
    var ref = storageRef.child(`${currentFacilityId}/profileimg.jpg`);
    return new Promise<any>((res, rej) => {
      ref.put(file).then((snapshot) => {
        snapshot.ref.getDownloadURL().then((url) => {
          res(url);
        }).catch(err => {
          rej(err);
        });
      });
    });
  }

  public getTotalsforcurrentFacility(currentFacilityId) {
    let url = this.apiEP + '/getTotalsforFacilityWithAuth?id=' + currentFacilityId;
    return new Promise<any>((ress, rejj) => {
      firebase.auth().currentUser.getIdToken().then(token => {
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        this._http.get(url, { headers: headers }).toPromise().then(r => {
          ress(r);
        }).catch(e => {
          rejj(e);
        });
      });
    });
  }

  public getErroneousDevices() {
    return this.getCollectionList("Devices");
  }

  public getDevicedata(deviceID) {
    return this.getCollectionData("Devices", deviceID);
  }

  public updateDevicedata(deviceID, data) {
    this.updateCollectionData('Devices', deviceID, data);
  }

  public addNewRequest(data: Request) {
    return new Promise<any>((res, rej) => {
      const statsRef = this.db.collection('Requests').doc('stats');
      const currentFacilityId = data.facilityId;
      const customerRef = this.db.collection('SQACustomers').doc(currentFacilityId);
      const increment = firebase.firestore.FieldValue.increment(1);
      const batch = this.db.batch();
      const reqRef = this.db.collection('Requests').doc();
      batch.set(reqRef, data);
      batch.set(statsRef, { requestsCount: increment }, { merge: true });
      batch.set(customerRef, { requestCount: increment }, { merge: true });
      batch.commit().then(val => {
        res(val);
      }).catch(err => {
        rej(err);
      });
    });
  }

  public getAllRequests() {
    return this.getCollectionList("Requests");
  }

  public async getAllRequestsForFacilities(facilityIds: string[]) {
    const ids = [...facilityIds];
    const promiseAll = [];

    while (ids.length !== 0) {
      const batch = ids.splice(0, 10);
      promiseAll.push(
        this.db.collection('Requests').where('facilityId', 'in', batch).get()
      );
    }

    const results = await Promise.all(promiseAll);
    const markers = [];

    results.forEach((querySnapshot) => {
      querySnapshot.forEach((doc) => {
        markers.push({ id: doc.id, data: doc.data() });
      });
    });

    return markers;
  }

  public getRequestsForFacility(id) {
    return new Promise<any>((res, rej) => {
      const markers: any = [];
      this.db.collection('Requests').where("facilityId", "==", id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            markers.push({ id: doc.id, data: doc.data() });
          });
          res(markers);
        });
    });
  }

  public getFailedSelfTestCount(deviceSNs: string[]): Promise<number> {
    const promises = deviceSNs.map(sn =>
      this.db.collection('Devices').doc(sn).collection('servicedata')
        .orderBy('serviceDataDate', 'desc').limit(1).get()
        .then(snapshot => {
          if (snapshot.docs.length > 0) {
            const params = snapshot.docs[0].data().serviceDataParameters;
            return params?.SelfTest === 0 ? 1 : 0;
          }
          return 0;
        })
        .catch(() => 0)
    );
    return Promise.all(promises).then(results => results.reduce((sum, val) => sum + val, 0));
  }

  public getOpenSupportCalls(facilityIds: string[]): Promise<number> {
    return this.db.collection('Requests')
      .where('status', '==', 'new')
      .get()
      .then(snapshot => {
        const facilitySet = new Set(facilityIds);
        let count = 0;
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.facilityId && facilitySet.has(data.facilityId)) {
            count++;
          }
        });
        return count;
      });
  }

  public updateRequest(id, data) {
    return this.updateCollectionData('Requests', id, data);
  }

  public deleteRequest(id) {
    return this.deleteCollectionItem('Requests', id);
  }

  public async updateTestCreditItem(creditId, creditData) {
    const doc = await this.afs.collection('Credits').doc(creditId).get().pipe(take(1)).toPromise();
    if (doc.data().status === 'unused') {
      await this.afs.collection('Credits').doc(creditId).set(creditData, { merge: true });
      return { id: creditId, data: creditData };
    } else {
      throw 'Cannot reset status!';
    }
  }

  public createTestCreditBatch(amount: number, creditCount: number, creditType: string = 'Development') {
    return new Promise<any>((res, rej) => {
      let admin = {
        adminEmail: this.currentAdmin.email,
        adminId: this.currentAdmin.uid
      };
      let currentBatchId = formatDate(new Date(), 'yyyy-MM-dd_HH:mm:ss', 'en');
      let batchArr: any = [];
      for (let index = 0; index < amount; index++) {
        let item: TestCredit = {
          status: 'unused',
          credit: creditCount,
          creator: admin,
          creationDate: new Date(),
          type: creditType,
          batchId: currentBatchId
        };

        this.addCollectionItam("Credits", item).then(doc => {
          batchArr.push({ 'id': doc.id, 'data': item });
        }).catch(err => {
          rej(err);
        });
      }

      let itemData: CreditBatch = {
        creator: admin,
        type: creditType,
        creditNumber: creditCount,
        creditAmount: amount,
        printLock: false
      };
      this.afs.collection('CreditBatch').doc(currentBatchId).set(itemData)
        .catch((error) => {
          console.error("Error adding document: ", error);
          rej(error);
        });

      res(batchArr);
    });
  }

  public getListOfTestCreditBatches() {
    return this.getCollectionList('CreditBatch');
  }

  public getCreditItemsOfBatch(batchId: string) {
    return new Promise<any>((res, rej) => {
      const markers: any = [];
      this.db.collection('Credits').where("batchId", "==", batchId)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            markers.push({ id: doc.id, data: doc.data() });
          });
          res(markers);
        })
        .catch(err => {
          rej(err);
        });
    });
  }

  public deleteTestCreditBatch(id) {
    return new Promise<any>((res, rej) => {
      this.deleteCollectionItem('CreditBatch', id)
        .then(batchItem => {
          this.db.collection('Credits').where("batchId", "==", id).get()
            .then(snapShot => {
              snapShot.forEach(doc => {
                this.deleteCollectionItem('Credits', doc.id);
              });
              res(batchItem);
            })
            .catch(err => {
              rej(err);
            });
        })
        .catch(err => {
          rej(err);
        });
    });
  }

  public updateTestCreditBatch(id, data) {
    return this.updateCollectionData('CreditBatch', id, data);
  }

  public addNewTestCreditItem(amount: number, creditType: string = "Production") {
    let admin = {
      adminEmail: this.currentAdmin.email,
      adminId: this.currentAdmin.uid
    };
    let item: TestCredit = {
      status: 'unused',
      credit: amount,
      creator: admin,
      creationDate: new Date(),
      type: creditType,
    };
    return this.addCollectionItam("Credits", item);
  }

  public deleteTestCreditItem(id: string) {
    return new Promise<any>((res, rej) => {
      this.db.collection('Credits').doc(id).get()
        .then(snapshot => {
          let batchId = snapshot.data()["batchId"];
          if (batchId !== undefined) {
            const decrement = firebase.firestore.FieldValue.increment(-1);
            this.db.collection('CreditBatch').doc(batchId).set({ creditAmount: decrement }, { merge: true })
              .then(() => {
                this.deleteCollectionItem('Credits', id).then(creditItem => {
                  res(creditItem);
                }).catch(err => {
                  rej(err);
                });
              }).catch(err => {
                rej(err);
              });
          } else {
            this.deleteCollectionItem('Credits', id).then(creditItem => {
              res(creditItem);
            }).catch(err => {
              rej(err);
            });
          }
        });
    });
  }

  public getAllTestCredits() {
    return this.getCollectionList("Credits");
  }

  public getAllTestCreditsByFilter(status: string, typeArr, creatorIsMe: boolean) {
    return new Promise<any>((res, rej) => {
      let markers = [];
      if (creatorIsMe) {
        this.db.collection('Credits')
          .where('creator.adminId', '==', this.currentAdmin.uid)
          .where('status', '==', status)
          .where('type', 'in', typeArr)
          .get().then(querySnapshot => {
            querySnapshot.forEach(doc => {
              markers.push({ id: doc.id, data: doc.data() });
            });
            res(markers);
          }).catch(err => {
            rej(err);
          });
      } else {
        this.db.collection('Credits')
          .where('status', '==', status)
          .where('type', 'in', typeArr)
          .get().then(querySnapshot => {
            querySnapshot.forEach(doc => {
              markers.push({ id: doc.id, data: doc.data() });
            });
            res(markers);
          }).catch(err => {
            rej(err);
          });
      }
    });
  }

  public redeemTestCredits(creditId, facilityID, facilityNAME) {
    return new Promise<any>(res => {
      let currentFacilityCredits: number;
      let creditsToAdd: number = 0;

      this.getTestCreditItem(creditId).then((creditItem) => {
        if (creditItem.toString().includes('Error')) {
          res(creditItem);
        } else {
          creditsToAdd = Number(creditItem['data']['credit']);
          return this.getFacilityCurrentCredits(facilityID);
        }
      })
        .then((facilityCredits) => {
          if (facilityCredits.toString().includes('Error')) {
            res(facilityCredits);
          } else {
            currentFacilityCredits = Number(facilityCredits['testCredits']);
            let newCreditNumber: number = currentFacilityCredits + creditsToAdd;
            let data = { testCredits: newCreditNumber };
            return this.updateFacilityCredits(facilityID, data);
          }
        })
        .then(() => {
          let facility = {
            facilityId: facilityID,
            facilityName: facilityNAME
          };
          let admin = {
            email: this.currentAdmin.email,
            id: this.currentAdmin.uid,
            userType: 'admin'
          };
          let data = { status: 'used', usingFacility: facility, redeemingDate: new Date(), redeemedBy: admin };
          return this.updateTestCreditItem(creditId, data);
        })
        .then(() => {
          res('test credits updated successfully!');
        })
        .catch((err) => {
          console.error('redeem credits err', err);
          res(err);
        });
    });
  }

  private async getTestCreditItem(creditId) {
    const docRef = await this.afs.collection('Credits').doc(creditId).get().pipe(take(1)).toPromise();
    if (docRef.exists) {
      let credit = docRef.data();
      if (credit !== undefined) {
        if (credit['status'] === 'unused') {
          return { id: creditId, data: docRef.data() };
        } else {
          return 'Error: Credit has already been used!';
        }
      }
    }
    return 'Error: No such document!';
  }

  private async getFacilityCurrentCredits(facilityid) {
    const doc = await this.afs.collection('SQACustomers').doc(facilityid).get().pipe(take(1)).toPromise();
    if (doc.exists) {
      let data = doc.data();
      if (data !== undefined) {
        return { id: facilityid, testCredits: data['testCredits'] };
      }
      return "Error: Empty document!";
    }
    return "Error: No such document!";
  }

  private updateFacilityCredits(facilityid, data) {
    return this.updateCollectionData('SQACustomers', facilityid, data);
  }

  public async getTotalCustomerNumbers() {
    const querySnapshot = await this.afs.collection('SQACustomers').get().pipe(take(1)).toPromise();
    let totalCustomers = querySnapshot.docs.length;
    let patientscounter = 0;
    let testscounter = 0;
    querySnapshot.forEach(facility => {
      if (facility.data().testCount !== undefined && facility.data().patientCount !== undefined) {
        testscounter += facility.data().testCount;
        patientscounter += facility.data().patientCount;
      }
    });
    return { customers: totalCustomers, patients: patientscounter, tests: testscounter };
  }

  public getTotalRequestsNumber() {
    return this.getTotalNumber('Requests');
  }

  public getNewRequestsCount(facilityIds: string[]): Promise<number> {
    return this.db.collection('Requests').where('status', '==', 'new')
      .get()
      .then(snapshot => {
        const facilitySet = new Set(facilityIds);
        let count = 0;
        snapshot.forEach(doc => {
          if (facilitySet.has(doc.data().facilityId)) count++;
        });
        return count;
      });
  }

  public getTotalCalibrationsNumber() {
    return this.getTotalNumber('Calibrations');
  }

  public getTotalDevicesNumber() {
    return this.getTotalNumber('Devices');
  }

  public getTotalAdminsNumber() {
    return this.getTotalNumber('MESUsers');
  }

  public getTotalCreditsNumber() {
    return this.getTotalNumber('Credits');
  }

  public getTotalTests() {
    return new Promise<any>((res, rej) => {
      let markers: any = [];
      this.db.collectionGroup('tests').get()
        .then(tests => {
          for (const test of tests.docs) {
            let path = test.ref.path.split('/');
            let facility = path[1];
            let patient = path[3];
            if (test.id !== 'stats') {
              markers.push({ facilityid: facility, patientid: patient, testid: test.id, data: test.data() });
            }
          }
          res(markers);
        }).catch(err => {
          rej(err);
        });
    });
  }

  public getTotalPatients() {
    return new Promise<any>((res, rej) => {
      this.db.collectionGroup('patients').get()
        .then(patients => {
          let markers: any = [];
          for (const patient of patients.docs) {
            let path = patient.ref.path.split('/');
            let facility = path[1];
            markers.push({ facilityid: facility, patientid: patient.id, data: patient.data() });
          }
          res(markers);
        }).catch(err => {
          rej(err);
        });
    });
  }

  public addNewAdmin(data) {
    const url = this.apiEP + '/addNewAdminUser';
    return new Promise<any>((ress, rejj) => {
      this._http.post(url, data).toPromise().then((res) => {
        ress(res);
      }).catch(err => {
        if (err.error.code === 'auth/email-already-exists') {
          rejj(new EmailNotAvailableError());
        } else {
          rejj(err);
        }
      });
    });
  }

  public changeFacilityPassword(id: string, newPassword) {
    const data = JSON.stringify({ password: newPassword });
    const url = this.apiEP + '/changeFacilityPasswordWithAuth?id=' + id;
    return new Promise<any>((ress, rejj) => {
      firebase.auth().currentUser.getIdToken().then(token => {
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this._http.post(url, data, { headers: headers }).toPromise().then((res) => {
          ress(res);
        }).catch(err => {
          rejj(err);
        });
      });
    });
  }

  public getFacilitiesByType() {
    return new Promise<any>((res, rej) => {
      this.getallCustomers().then(facilities => {
        let facilityIds = [];
        let distributorIds = [];
        let demoIds = [];
        let alphaIds = [];
        let developmentIds = [];
        let validationIds = [];
        let marketingIds = [];
        let clinicalIds = [];
        let undefinedIds = [];

        for (let i = 0; i < facilities.length; i++) {
          let facility = facilities[i];
          switch (facility.data.customerType) {
            case 'facility':
              facilityIds.push(facility.id);
              break;
            case 'distributor':
              distributorIds.push(facility.id);
              break;
            case 'demo':
              demoIds.push(facility.id);
              break;
            case 'alpha':
              alphaIds.push(facility.id);
              break;
            case 'development':
              developmentIds.push(facility.id);
              break;
            case 'validation':
              validationIds.push(facility.id);
              break;
            case 'marketing':
              marketingIds.push(facility.id);
              break;
            case 'clinical trial':
              clinicalIds.push(facility.id);
              break;
            default:
              undefinedIds.push(facility.id);
              break;
          }
        }
        res({
          facility: facilityIds,
          demo: demoIds,
          alpha: alphaIds,
          development: developmentIds,
          validation: validationIds,
          marketing: marketingIds,
          clinical: clinicalIds,
          notdef: undefinedIds,
          distributor: distributorIds
        });
      }).catch(err => { rej(err); });
    });
  }

  public getAllAverageTestsPerDay() {
    return new Promise<any>((res, rej) => {
      const allDates: any = [];
      let distinctDates: any = [];
      const avrgTests: any = [];

      this.db.collectionGroup('tests').get().then(tests => {
        for (const test of tests.docs) {
          try {
            let timestamp = test.data().dateTime.toDate();
            let element = formatDate(new Date(timestamp), 'yyyy/MM/dd', 'en');
            allDates.push(element);
          } catch (error) {
            // skip tests with invalid date
          }
        }

        const distinct = (value: any, index: any, self: any) => {
          return self.indexOf(value) === index;
        };
        distinctDates = allDates.filter(distinct);
        distinctDates.sort();

        let days = 1;
        let test_count = 0;
        for (const date of distinctDates) {
          let daily_test_count = 0;
          for (const testDate of allDates) {
            if (testDate === date) {
              test_count++;
              daily_test_count++;
            }
          }
          let avrg = test_count / days;
          avrgTests.push({ Date: date, DailyTests: daily_test_count, TotalTests: test_count, Days: days, Avrg: avrg });
          days++;
        }
        res(avrgTests);
      }).catch(err => {
        rej(err);
      });
    });
  }

  public addDeviceServiceData(deviceID, data) {
    return new Promise<any>((res, rej) => {
      const statsRef = this.db.collection('Devices').doc(deviceID);
      const increment = firebase.firestore.FieldValue.increment(1);
      const batch = this.db.batch();
      const reqRef = this.db.collection('Devices').doc(deviceID).collection('servicedata').doc();
      batch.set(reqRef, data);
      batch.set(statsRef, { selfTestCount: increment }, { merge: true });
      batch.commit().then(val => {
        res(val);
      }).catch(err => {
        rej(err);
      });
    });
  }

  public updateFacilityLocation(facilityId, latitude, longitude) {
    return new Promise<any>((res, rej) => {
      const location = {
        lat: latitude,
        lng: longitude
      };
      this.afs.doc(`SQACustomers/${facilityId}`).set({ geoLocation: location }, { merge: true }).then(() => {
        res(location);
      }).catch(err => {
        rej(err);
      });
    });
  }

  public updateDeviceLocation(deviceSN, latitude, longitude) {
    return new Promise<any>((res, rej) => {
      const deviceData: Device = {};
      const location = {
        lat: latitude,
        lng: longitude
      };
      deviceData.usingFacility = {
        facilityAddress: {
          geo: location
        }
      };
      this.afs.doc(`Devices/${deviceSN}`).set(deviceData, { merge: true }).then(() => {
        res(location);
      }).catch(err => {
        rej(err);
      });
    });
  }

  public getFacilityTotalCredits(facilityId) {
    return new Promise<any>((res, rej) => {
      this.db.collection(`Credits`).where('usingFacility.facilityId', '==', facilityId).get().then(dox => {
        let credits = 0;
        for (const doc of dox.docs) {
          credits = credits + doc.data().credit;
        }
        res(credits);
      }).catch(err => {
        rej(err);
      });
    });
  }

  public AddNotification(data) {
    return this.addCollectionItam('Notifications', data);
  }

  public DeleteNotification(notificationId) {
    return this.deleteCollectionItem('Notifications', notificationId);
  }

  public UpdateNotification(notificationId, data) {
    return this.updateCollectionData('Notifications', notificationId, data);
  }

  public getAllNotifications() {
    return this.getCollectionList('Notifications');
  }

  public AddDistributer(data) {
    return this.addCollectionItam('Distributers', data);
  }

  public DeleteDistributer(code) {
    return this.deleteCollectionItem('Distributers', code);
  }

  public UpdateDistributer(code, data) {
    return this.updateCollectionData('Distributers', code, data);
  }

  public getDistributerById(id: string) {
    return this.getCollectionData('Distributers', id);
  }

  public getAllDistributers() {
    return this.getCollectionList('Distributers');
  }

  public getAllDistributerCount() {
    return new Promise<any>((res, rej) => {
      this.db.collection('Distributers').get().then(snap => {
        res(snap.size);
      }).catch(() => {
        res(0);
      });
    });
  }

  public getMESDistributers() {
    return this.getCollectionList('MESDistributers');
  }

  public addQCBatch(data: ControlsBatch) {
    let dData: ControlsBatch = {
      ...data,
      adminEditor: {
        id: this.currentAdmin.uid,
        email: this.currentAdmin.email,
        name: this.currentAdmin.name,
        dateTime: new Date()
      }
    };
    return new Promise<any>((res, rej) => {
      this.db.collection('Controls').doc(data.batchID).set(dData)
        .then(() => {
          res(dData);
        })
        .catch((error) => {
          console.error("Error adding document: ", error);
          rej(error);
        });
    });
  }

  public DeleteQCBatch(batchId) {
    return this.deleteCollectionItem('Controls', batchId);
  }

  public UpdateQCBatch(batchId, data: ControlsBatch) {
    let dData: ControlsBatch = {
      ...data,
      adminEditor: {
        id: this.currentAdmin.uid,
        email: this.currentAdmin.email,
        name: this.currentAdmin.name,
        dateTime: new Date()
      }
    };
    return this.updateCollectionData('Controls', batchId, dData);
  }

  public getAllQCControls() {
    return this.getCollectionList('Controls');
  }

  public getUsersForFacility(facilityId) {
    return new Promise<any>((res, rej) => {
      let markers: any = [];
      this.db.collection('SQAUsers').where("facilityID", "==", facilityId).get().then(users => {
        for (const user of users.docs) {
          markers.push({ id: user.id, data: user.data() });
        }
        res(markers);
      }).catch(e => {
        rej(e);
      });
    });
  }

  public getTotalQCTests() {
    return new Promise<any>((res, rej) => {
      let markers: any = [];
      this.db.collectionGroup('controlTests').get()
        .then(tests => {
          for (const test of tests.docs) {
            let path = test.ref.path.split('/');
            let facility = path[1];
            if (test.id !== 'stats') {
              markers.push({ facilityid: facility, testid: test.id, data: test.data() });
            }
          }
          res(markers);
        }).catch(err => {
          rej(err);
        });
    });
  }

  public getProficiencyBatch(id) {
    return this.getCollectionData('Proficiency', id);
  }

  public getTotalProficiencyTests() {
    return new Promise<any>((res, rej) => {
      let markers: any = [];
      this.db.collectionGroup('proficiencyTests').get()
        .then(tests => {
          for (const test of tests.docs) {
            let path = test.ref.path.split('/');
            let facility = path[1];
            if (test.id !== 'stats') {
              markers.push({ facilityid: facility, testid: test.id, data: test.data() });
            }
          }
          res(markers);
        }).catch(err => {
          rej(err);
        });
    });
  }

  public getAllUsers() {
    return this.getCollectionList('SQAUsers');
  }

  public getAlltests() {
    return this._http.get<{ status: string; data: { id: string; data: any }[] }>(`${this.apiUrl}/getAllTests`)
      .pipe(take(1)).toPromise();
  }

  public getTestsPerMonth(facilityIds: string[]): Promise<{ month: string; count: number }[]> {
    return this._http.post<{ status: string; data: { month: string; count: number }[] }>(
      `https://iodistributorervices-1022282159297.us-central1.run.app/getTestsPerMonth`, { facilityIds }
    ).pipe(take(1)).toPromise().then(res => res.data);
  }

  evaluateCountryName({ facilityId, lat, lng }) {
    const data = {
      "facilityID": facilityId,
      "lat": lat,
      "lng": lng
    };
    return this._http.post<{ status: string; data: { id: string; data: any }[] }>(`${this.apiUrl}/getcountry`, data)
      .pipe(take(1)).toPromise();
  }

  getLastServiceDataOfDevice(deviceId): Promise<{ id: string, data: any }> {
    return new Promise<{ id: string, data: any }>((res, rej) => {
      this.db.collection('Devices').doc(deviceId).collection("servicedata").orderBy('serviceDataDate', 'desc').limit(1).get().then(result => {
        const test = result.docs[0];
        res({ id: test.id, data: test.data() });
      }).catch(err => {
        rej(err);
      });
    });
  }

  getFirstorLastTest(facilityId, direction) {
    return new Promise((res, rej) => {
      this.db.collectionGroup('tests').where("facilityId", "==", facilityId).orderBy("dateTime", direction).limit(1).get().then(tests => {
        const test = tests.docs[0];
        res({ id: test.id, data: test.data() });
      }).catch(err => {
        rej(err);
      });
    });
  }
}
